/**
 * Crunch Design System — Combobox
 *
 * Searchable select. Composes .cr-input and .cr-menu, matching the vanilla cr-combobox used in
 * UniversalConnector.UploadPortal (_AccountingSystemModal.cshtml + shared-accounting-system-modal.js).
 *
 * Two mutually-exclusive rendered states, mirroring that markup:
 *   - committed value  -> a chip (.cr-combobox__value) with a clear (x) button
 *   - search mode       -> a text input + chevron with a filtered dropdown listbox
 *
 * Unlike the accounting-system combobox this does not offer a free-text "custom" row or a fuzzy
 * suggestion: the value must be one of `options` (e.g. a NAICS code).
 *
 * Usage:
 *   <Combobox
 *       options={codes.map(c => ({ value: String(c.code), label: `${c.code} - ${c.description}` }))}
 *       value={selected}
 *       onChange={setSelected}
 *       placeholder="Search NAICS code or industry"
 *   />
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faXmark } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { Menu, MenuItem } from './Menu';

export type ComboboxOption = {
    value: string;
    label: string;
};

export function Combobox({
    options,
    value,
    onChange,
    placeholder,
    id,
    error = false,
    disabled = false,
    className,
}: {
    options: ComboboxOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    id?: string;
    error?: boolean;
    disabled?: boolean;
    className?: string;
}) {
    const [searchText, setSearchText] = React.useState<string>('');
    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [highlightIndex, setHighlightIndex] = React.useState<number>(-1);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const listboxRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const listboxId = `${React.useId()}-listbox`;

    const selectedOption = options.find(option => option.value === value);
    const hasValue = !!selectedOption;

    const query = searchText.trim().toLowerCase();
    const filtered = query
        ? options.filter(option => option.label.toLowerCase().includes(query))
        : options;

    // Keep the highlighted row scrolled into the capped-height listbox's viewport.
    React.useEffect(() => {
        if (!isOpen) {
            return;
        }
        listboxRef.current
            ?.querySelector('.cr-menu-item--active')
            ?.scrollIntoView({ block: 'nearest' });
    }, [highlightIndex, isOpen]);

    const commit = (optionValue: string): void => {
        setSearchText('');
        setIsOpen(false);
        setHighlightIndex(-1);
        onChange(optionValue);
    };

    // Clearing drops back to search mode: empty text, no committed value, list open, input focused. Used
    // by both the clear (x) button and clicking a committed selection. isOpen is set explicitly rather
    // than relying on focus, since the input may already be focused (its onFocus wouldn't refire).
    const clear = (): void => {
        setSearchText('');
        onChange('');
        setIsOpen(true);
        window.requestAnimationFrame(() => inputRef.current?.focus());
    };

    // Clicking anywhere in the shell that isn't the input itself (the chevron, or padding) focuses the
    // input and opens the list — so the chevron opens the dropdown without being its own button. Skipped
    // in the committed state, where the clear (x) button handles interaction. preventDefault keeps focus
    // on the input rather than the clicked element.
    const onShellMouseDown = (e: React.MouseEvent): void => {
        if (disabled || hasValue || e.target === inputRef.current) {
            return;
        }
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
    };

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // With a committed value the input is read-only and shows the selection; Backspace/Delete clears
        // it back to search mode.
        if (hasValue) {
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                clear();
            }
            return;
        }

        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightIndex(current =>
                    filtered.length === 0 ? -1 : (current + 1) % filtered.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightIndex(current =>
                    filtered.length === 0 ? -1 : (current - 1 + filtered.length) % filtered.length);
                break;
            case 'Enter':
                if (highlightIndex >= 0 && highlightIndex < filtered.length) {
                    e.preventDefault();
                    commit(filtered[highlightIndex].value);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightIndex(-1);
                break;
        }
    };

    const shellClasses = [
        'cr-input',
        error && 'cr-input--danger',
        disabled && 'cr-input--disabled',
    ]
        .filter(Boolean)
        .join(' ');

    const listboxOpen = isOpen && !hasValue && filtered.length > 0;

    return (
        <div className={['cr-combobox', className].filter(Boolean).join(' ')} ref={containerRef}>
            <div className={shellClasses} onMouseDown={onShellMouseDown}>
                {/* One input across both states: it shows the search text while typing, or the committed
                option's label (read-only) once a value is picked. */}
                <input
                    type={'text'}
                    className={'cr-input__control'}
                    id={id}
                    ref={inputRef}
                    role={'combobox'}
                    aria-expanded={listboxOpen}
                    aria-controls={listboxId}
                    aria-autocomplete={'list'}
                    autoComplete={'off'}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={hasValue}
                    value={hasValue ? (selectedOption?.label ?? '') : searchText}
                    onChange={(e) => {
                        setSearchText(e.currentTarget.value);
                        setIsOpen(true);
                        setHighlightIndex(-1);
                    }}
                    onFocus={() => {
                        if (!hasValue) {
                            setIsOpen(true);
                        }
                    }}
                    onClick={() => {
                        if (hasValue) {
                            // Clicking a committed selection clears it and drops the list back open
                            // (clear() refocuses, which reopens) — matching the exemplar's search mode.
                            clear();
                        } else {
                            // Clicking the field opens the list too — and reopens it when the input is
                            // already focused (e.g. after Escape), where onFocus wouldn't fire again.
                            setIsOpen(true);
                        }
                    }}
                    onBlur={(e) => {
                        // Only close when focus actually leaves the combobox. When it moves to a child (a
                        // list item or the clear button), keep the list open so that element's click/commit
                        // can run — the blur would otherwise unmount the list first and swallow the click.
                        if (containerRef.current?.contains(e.relatedTarget as Node | null)) {
                            return;
                        }
                        setIsOpen(false);
                    }}
                    onKeyDown={onInputKeyDown}
                />
                {hasValue ? (
                    // Committed: the chevron slot becomes the clear (x) button — a bare button sized by the
                    // 25x25 .cr-input__icon slot (its chrome reset in .cr-combobox__clear).
                    <button
                        type={'button'}
                        className={'cr-input__icon cr-combobox__clear'}
                        aria-label={'Clear selection'}
                        disabled={disabled}
                        tabIndex={-1}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                            e.stopPropagation();
                            clear();
                        }}
                    >
                        <FontAwesomeIcon icon={faXmark} aria-hidden />
                    </button>
                ) : (
                    // Search mode: chevron in the 25x25 icon slot. It isn't its own control — the shell's
                    // mousedown handler focuses the input and opens the list when it's clicked.
                    <span className={'cr-input__icon cr-combobox__chevron'} aria-hidden>
                        <FontAwesomeIcon icon={faAngleDown} />
                    </span>
                )}
            </div>
            {listboxOpen && (
                <Menu
                    className={'cr-combobox__listbox'}
                    id={listboxId}
                    role={'listbox'}
                    ref={listboxRef}
                    // Suppress the default focus shift so the input keeps focus and a row's click commits
                    // instead of a blur unmounting the list first. pointerdown (not mousedown) so this also
                    // holds for touch; the input's onBlur guard is the backstop if focus still moves.
                    onPointerDown={(e: React.PointerEvent) => e.preventDefault()}
                >
                    {filtered.map((option, index) => (
                        <MenuItem
                            key={option.value}
                            role={'option'}
                            active={index === highlightIndex}
                            onClick={() => commit(option.value)}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </div>
    );
}
